import { useEffect, useRef, useState } from 'react'
import { Capacitor } from '@capacitor/core'
import { FlightMapNative, type FlightMapMode } from '@/lib/native/flight-map'
import type { Airport } from '@/lib/airport-db'
import { Globe, Map } from '@/components/icons'
import { FlightMapWeb } from './FlightMapWeb'
import { FlightMapGoogle } from './FlightMapGoogle'
import { useTransparentAppBackground } from '@/lib/use-transparent-background'

interface FlightMapProps {
  departure?: Airport
  arrival?: Airport
  departureDateTime?: Date | string
  arrivalDateTime?: Date | string
  /**
   * Single-point marker shown when no `departure`/`arrival` route is provided.
   * Used on Home when the crew has no upcoming flight: drops a pin at the
   * device's current location with the city name as the pill label.
   */
  location?: { latitude: number; longitude: number; label?: string }
  showModeToggle?: boolean
  /**
   * Optional controlled mode. When provided, the parent owns the globe/flat
   * state (e.g. driven by a toggle in the top bar) and changes are pushed to
   * the native map. When omitted, the map manages its own internal mode.
   */
  mode?: FlightMapMode
  onModeChange?: (mode: FlightMapMode) => void
  interactive?: boolean
  cameraAltitude?: number
  edgePadding?: { top: number; left: number; bottom: number; right: number }
  realisticElevation?: boolean
}

/**
 * Picks the right map renderer for the platform: the native MKMapView (behind
 * the webview, via the `FlightMap` Capacitor plugin) on iOS, the native Google
 * Map (`FlightMapGoogle`, via @capacitor/google-maps) on Android, or Apple
 * MapKit JS (`FlightMapWeb`) on desktop web — the MapKit JS token is
 * origin-locked to *.pronteer.com, so it only authorizes there.
 */
export function FlightMap(props: FlightMapProps) {
  const platform = Capacitor.getPlatform()
  if (platform === 'ios') return <FlightMapNativeView {...props} />

  const Renderer = platform === 'android' ? FlightMapGoogle : FlightMapWeb
  return (
    <Renderer
      departure={props.departure}
      arrival={props.arrival}
      departureDateTime={props.departureDateTime}
      arrivalDateTime={props.arrivalDateTime}
      location={props.location}
      interactive={props.interactive}
      edgePadding={props.edgePadding}
    />
  )
}

function FlightMapNativeView({
  departure,
  arrival,
  departureDateTime,
  arrivalDateTime,
  location,
  showModeToggle = false,
  mode: controlledMode,
  onModeChange,
  interactive = true,
  cameraAltitude,
  edgePadding,
  realisticElevation,
}: FlightMapProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const pillRef = useRef<HTMLButtonElement>(null)
  const [internalMode, setInternalMode] = useState<FlightMapMode>('globe')
  const mode = controlledMode ?? internalMode
  // Keep a ref to the current mode so the show() effect can read it at mount
  // time without resubscribing every time the user flips the toggle.
  const modeRef = useRef<FlightMapMode>(mode)
  modeRef.current = mode

  // When the parent controls the mode (e.g. top-bar toggle), push changes to
  // the native map.
  useEffect(() => {
    if (controlledMode === undefined) return
    FlightMapNative.setMode({ mode: controlledMode }).catch((e) =>
      console.error('FlightMap setMode failed', e)
    )
  }, [controlledMode])

  // While the native MKMapView is mounted behind the webview, make body + #root
  // transparent so the map shows through.
  useTransparentAppBackground()

  const hasRoute = !!(departure && arrival && departureDateTime && arrivalDateTime)

  useEffect(() => {
    if (!containerRef.current) return
    const el = containerRef.current
    let active = true

    const rect = () => {
      const r = el.getBoundingClientRect()
      return { x: r.x, y: r.y, width: r.width, height: r.height }
    }

    const route = hasRoute
      ? {
          departure: {
            lat: departure!.latitudeDeg,
            lon: departure!.longitudeDeg,
            icao: departure!.icaoCode,
          },
          arrival: {
            lat: arrival!.latitudeDeg,
            lon: arrival!.longitudeDeg,
            icao: arrival!.icaoCode,
          },
          departureDateTime: new Date(departureDateTime!).toISOString(),
          arrivalDateTime: new Date(arrivalDateTime!).toISOString(),
        }
      : {}

    const locationOpt = !hasRoute && location
      ? { location: { lat: location.latitude, lon: location.longitude, label: location.label } }
      : {}

    FlightMapNative.show({
      frame: rect(),
      mode: modeRef.current,
      interactive,
      ...(cameraAltitude !== undefined && { cameraAltitude }),
      ...(edgePadding !== undefined && { edgePadding }),
      ...(realisticElevation !== undefined && { realisticElevation }),
      ...route,
      ...locationOpt,
    }).catch((e) => console.error('FlightMap show failed', e))

    const syncFrame = () => {
      if (!active) return
      FlightMapNative.setFrame(rect()).catch(() => {})
    }
    const ro = new ResizeObserver(syncFrame)
    ro.observe(el)
    window.addEventListener('resize', syncFrame)
    window.addEventListener('orientationchange', syncFrame)

    return () => {
      active = false
      ro.disconnect()
      window.removeEventListener('resize', syncFrame)
      window.removeEventListener('orientationchange', syncFrame)
      FlightMapNative.hide().catch(() => {})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasRoute,
    interactive,
    cameraAltitude,
    edgePadding?.top,
    edgePadding?.left,
    edgePadding?.bottom,
    edgePadding?.right,
    realisticElevation,
    departure?.icaoCode,
    departure?.latitudeDeg,
    departure?.longitudeDeg,
    arrival?.icaoCode,
    arrival?.latitudeDeg,
    arrival?.longitudeDeg,
    departureDateTime,
    arrivalDateTime,
    location?.latitude,
    location?.longitude,
    location?.label,
  ])

  useEffect(() => {
    let cancelled = false
    let remove: (() => void) | undefined
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(FlightMapNative as unknown as { addListener: (event: string, cb: (data: unknown) => void) => Promise<{ remove: () => void }> })
      .addListener('cameraChanged', () => {})
      .then((handle) => {
        if (cancelled) handle.remove()
        else remove = () => handle.remove()
      })
      .catch(() => {})
    return () => {
      cancelled = true
      remove?.()
    }
  }, [])

  // Register the toggle pill's rect with the native passthrough webview so taps
  // on it are captured by the webview instead of falling through to the map.
  useEffect(() => {
    if (!showModeToggle) return
    const el = pillRef.current
    if (!el) return

    const syncRect = () => {
      const r = el.getBoundingClientRect()
      if (r.width === 0 || r.height === 0) return
      FlightMapNative.setControlRect({ x: r.x, y: r.y, width: r.width, height: r.height }).catch(() => {})
    }
    syncRect()

    const ro = new ResizeObserver(syncRect)
    ro.observe(el)
    window.addEventListener('resize', syncRect)
    window.addEventListener('orientationchange', syncRect)

    return () => {
      ro.disconnect()
      window.removeEventListener('resize', syncRect)
      window.removeEventListener('orientationchange', syncRect)
      FlightMapNative.clearControlRect().catch(() => {})
    }
  }, [showModeToggle])

  const toggleMode = () => {
    const next: FlightMapMode = mode === 'globe' ? 'flat' : 'globe'
    if (controlledMode === undefined) {
      setInternalMode(next)
      FlightMapNative.setMode({ mode: next }).catch((e) => console.error('FlightMap setMode failed', e))
    }
    onModeChange?.(next)
  }

  return (
    <div className="relative w-full h-full">
      <div ref={containerRef} className="w-full h-full" />
      {showModeToggle && (
        <button
          ref={pillRef}
          type="button"
          aria-label={mode === 'globe' ? 'Switch to flat map' : 'Switch to globe'}
          onClick={toggleMode}
          className="fixed right-4 top-32 z-50 size-12 rounded-full flex items-center justify-center bg-black/35 backdrop-blur-md transition-colors active:bg-black/50"
        >
          {mode === 'globe' ? (
            <Map className="size-5 text-white" />
          ) : (
            <Globe className="size-5 text-white" />
          )}
        </button>
      )}
    </div>
  )
}
